import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Available request date computation strategies.
 * Add new strategies here when implementing them in the backend registry.
 */
const REQUEST_DATE_STRATEGIES = [
  "inventory-lead-time",
  "shipping-method",
] as const;

/**
 * Compute Request Date Step
 *
 * Computes the request date (deliveryDate) for staged purchase orders based on
 * inventory validation results and line item lead times.
 *
 * Uses a configurable strategy pattern to allow different computation logic
 * per organization or use case.
 *
 * Formula (default strategy):
 *   - All items sufficient  -> today + 5 business days
 *   - Any item insufficient -> today + max(leadTimeDays) calendar days + 5 business days
 *
 * The step:
 * - Loads each staged PO and its line items
 * - Skips if deliveryDate is already set (user override)
 * - Resolves productVariantIds from customerProductIds if needed
 * - Calls the ERP adapter to validate inventory
 * - Applies the configured strategy to compute the date
 * - Updates the staged PO's deliveryDate field
 *
 * Note: This is a backend-only step that requires database and ERP adapter access.
 * The handler is registered in backend/src/workflows/steps/order-management/
 */
export const computeRequestDateStep = defineStepConfig({
  type: "compute_request_date",
  name: "Compute Request Date",
  description:
    "Computes request date for staged POs based on inventory status and lead times",
  category: "Data Access",
  libraryModule: "OrderManagement",
  deprecated: {
    reason:
      "Legacy staged-PO flow (computes request dates on staged POs). Superseded by the OMS v3 draft-sales-order flow; request-date handling moves to the draft/line model.",
  },
  inputSchema: z.object({
    stagedPurchaseOrderIds: z
      .array(z.string())
      .describe("UUIDs of the staged purchase orders to process"),
  }),

  outputSchema: z.object({
    stagedPurchaseOrderIds: z
      .array(z.string())
      .describe("UUIDs of the staged purchase orders (pass-through)"),
    processedCount: z
      .number()
      .int()
      .describe("Number of POs that had their request date computed"),
  }),

  configSchema: z.object({
    strategyName: z
      .enum(REQUEST_DATE_STRATEGIES)
      .default("inventory-lead-time")
      .describe("Name of the request date strategy to use"),
    disabled: z
      .boolean()
      .default(false)
      .describe("If true, skip computation and pass through original POs"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
